<script setup>
import {computed, inject} from "vue";
import exportsService from "@/services/exports.service";
import {download} from "@/helpers/file";
import {trackerTypes} from "@/constants/trackers";
import {ref} from "vue";
import ExportPdf from "@/components/export/ExportPdf.vue";
import {useStore} from "vue2-helpers/vuex";
import accountTypes from "@/constants/accountTypes";

const store = useStore();
const tracker = inject("tracker").value;
const companiesCount = inject("companiesCount");
const selectedRows = inject("selectedRows");
const selectedAllResults = inject("selectedAllResults");
const serverFilters = inject("serverFilters");
const columns = inject("columns");
const companyType = inject("addCellData").companyType;
const hasNotEmbargoed = inject("hasNotEmbargoed");
const selectedStartupName = inject("selectedStartupName");

const exportPdf = ref(null);
const tableColumnsType = computed(() => tracker.type === trackerTypes.ECOSYSTEMS ? 'tracker-ecosystems' : 'tracker-startups');

const canExportContacts = computed(() =>
    selectedRows.value.length &&
    (!store.getters.settings.watch_embargoed || hasNotEmbargoed.value)
);

async function exportCSV() {
    const {data} = await (
        selectedAllResults.value || !selectedRows.value.length
        ? exportsService.exportAllCompaniesFromTracker(tracker.id, serverFilters.value, columns.value, companyType)
        : exportsService.exportCompaniesFromTracker(tracker.id, selectedRows.value, columns.value, companyType)
    );

    if (!store.getters.isDaikinEnterprise && companiesCount.value > 1000 && selectedAllResults.value) {
        alert('Export limit exceeded: only the first 1,000 startup records were exported. Please narrow the dataset using filters.');
    }

    download(data, tracker.name + '.csv');
}

function openExportPdf() {
    exportPdf.value.openForTracker(tracker.name, tracker.id, tracker.type, selectedAllResults.value ? [] : selectedRows.value, serverFilters.value);
}

async function exportPPT(advanced = false) {
    let filters = {};
    let startupsIds = [];

    if (selectedAllResults.value) {
        filters = {
            ...serverFilters.value,
            tracker_id: tracker.id,
        }
    } else {
        startupsIds = selectedRows.value
    }

    const {data} = await exportsService.generateStartupsPpt(advanced, startupsIds, filters);

    if (selectedAllResults.value) {
        if (companiesCount.value > 500 && !advanced) {
            alert('Export limit exceeded: only the first 500 startup records were exported. Please narrow the dataset using filters.');
        }

        if (companiesCount.value > 125 && advanced) {
            alert('Export limit exceeded: only the first 125 startup records were exported. Please narrow the dataset using filters.');
        }
    }

    download(data, tracker.name + '.pptx');
}

async function exportContacts() {
    const {data} = await (
        selectedAllResults.value ?
            exportsService.exportAllCompaniesContactsFromTracker(tracker.id, serverFilters.value) :
            exportsService.exportCompaniesContacts(tracker.type === trackerTypes.ECOSYSTEMS ? accountTypes.ECOSYSTEM : accountTypes.STARTUP, selectedRows.value)
    )

    let fileName = (!selectedAllResults.value && selectedStartupName.value) ?
        selectedStartupName.value + '\'s contacts.csv' :
        tracker.name + ' - contacts.csv';

    download(data, fileName);
}

async function exportFundings() {
    const {data} = await (
        selectedAllResults.value ?
            exportsService.exportAllStartupsFundingsFromTracker(tracker.id, serverFilters.value) :
            exportsService.exportStartupsFundingsFromTracker(tracker.id, selectedRows.value)
    );

    download(data, tracker.name + ' - fundings.csv');
}
</script>

<template>
    <li v-if="tracker.type !== trackerTypes.ECOSYSTEMS || companiesCount" class="actions-list__export">
        <b-dropdown
            text="Export"
            class="dropdown-classic"
        >
            <b-dropdown-item
                v-if="companiesCount"
                class="icons-list__csv pt-2"
                @click="exportCSV"
            >
                Export CSV
            </b-dropdown-item>

            <b-dropdown-item
                v-if="tracker.type !== trackerTypes.ECOSYSTEMS"
                @click="openExportPdf"
                class="icons-list__pdf pt-2"
            >
                Export PDF
            </b-dropdown-item>

            <template v-if="tracker.type !== trackerTypes.ECOSYSTEMS">
                <b-dropdown-item
                    class="icons-list__ppt pt-2"
                    @click="exportPPT(false)"
                >
                    Export PPT
                </b-dropdown-item>

                <b-dropdown-item
                    v-if="$store.state.user.settings.allow_advanced_ppt_export"
                    class="icons-list__ppt pt-2"
                    @click="exportPPT(true)"
                >
                    Export PPT (Advanced)
                </b-dropdown-item>
            </template>

            <b-dropdown-item
                v-if="canExportContacts"
                @click="exportContacts" class="icons-list__csv pt-2"
            >
                Export Contacts
            </b-dropdown-item>

            <b-dropdown-item
                v-if="selectedRows.length && tracker.type !== trackerTypes.ECOSYSTEMS"
                @click="exportFundings"
                class="icons-list__csv pt-2"
            >
                Export Fundings
            </b-dropdown-item>
        </b-dropdown>

        <ExportPdf
            ref="exportPdf"
            :public-tracker="tracker.type === trackerTypes.PUBLIC"
            :tableType="tableColumnsType"
            :has-startups="!!companiesCount"
        />
    </li>
</template>

<style scoped>

</style>
